import { Card, Dropdown, Input, InputProps, Tooltip } from 'antd';
import { useEffect, useState, forwardRef } from 'react';
// import { getTodayDate } from 'utils/getTodayDate';
import { NEPALI_DAYS } from './data';
import CalendarHeader from './header';
import {
  formatDate,
  getMonthData,
  isBetween,
  isValidDateString,
  parseDateString,
  toDevanagari,
} from './utils';
import { CalendarOutlined } from '@ant-design/icons';
// import { CalendarIcon } from '../Icons';
import NepaliDate from 'nepali-date-converter';
import { getAdFromBS } from '../custom-date-selector copy/helpers';
import { getAdFromBSInYMD } from '../custom-date-selector-list/helpers';

export interface NepaliDatepickerProps extends Pick<InputProps, 'className'> {
  disabledBefore?: string;
  disabledAfter?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value?: string) => void;
  onKeyDown?: (value?: object) => void;
  readOnly?: boolean;
  prefixName?: string;
  allowClear?: boolean;
  suffixIcon?: any;
  disabled?: boolean;
  // ref?: any;
  selectRef?: any;
  datePickerFocusRef?: any;
  form?: any;
}

const NepaliDatePickerComponent = forwardRef<any, NepaliDatepickerProps>(
  (props, ref) => {
    const {
      disabledBefore,
      disabledAfter,
      value,
      readOnly,
      placeholder,
      onChange,
      onKeyDown,
      selectRef,
      form,
    } = props;

    const getTodayDate = () => {
      const todayBsDate = new NepaliDate();

      const bsTodayDate = `${todayBsDate.getYear()}-${(
        todayBsDate.getMonth() + 1
      )
        .toString()
        .padStart(
          2,
          '0',
        )}-${todayBsDate.getDate().toString().padStart(2, '0')}`;

      return bsTodayDate;
    };
    const today = getTodayDate();

    // #1CADE3 previous color

    // Styles
    const containerStyle = 'tw-my-[2px] tw-p-[2px]';
    const disabledContainerStyle = `tw-my-[2px] tw-p-[2px] tw-bg-[#F5F5F5]}`;
    const baseItemStyle =
      'tw-flex tw-items-center tw-justify-center tw-h-[24px] tw-w-[24px] tw-rounded-[4px]';
    const activeStyle = `tw-cursor-pointer hover:tw-bg-[#0000000a]`;
    const inactiveStyle = `tw-text-[#BFBFBF] tw-cursor-pointer ${activeStyle}`;
    const selectedStyle = 'tw-bg-[#10a96ff1] tw-cursor-pointer !tw-text-white';
    const disabledStyle = `tw-text-[#b8b8b8] dark:tw-text-[#616161] !tw-cursor-default !tw-rounded-none`;
    const todayStyle = 'tw-border-[#1CADE3] tw-cursor-pointer tw-border-[1px]';
    // Default State of Calendar
    const defaultPlaceholder = placeholder || 'Select date';

    // Local State is the current state of the datepicker
    const [localState, setLocalState] = useState<undefined | string>(
      isValidDateString(value ?? '') ? value : undefined,
    );
    // Display Date is the date that is displayed to the user
    const [displayDate, setDisplayedDate] = useState<undefined | string>(
      isValidDateString(value ?? '') ? value : undefined,
    );

    const [activePlaceholder, setActivePlaceholder] =
      useState(defaultPlaceholder);

    const [year, month] = parseDateString(localState ?? today);

    const [isOpen, setIsOpen] = useState(false);
    const [isCardFocused, setIsCardFocused] = useState(false);

    const changeDate = (e: any) => {
      if (!isOpen) setIsOpen(true);
      setDisplayedDate(e.target.value);
      if (isValidDateString(e.target.value)) {
        setLocalState(e.target.value);
        onChange && onChange(e.target.value);
      }
    };

    const onDateClicked = (date: string) => {
      setDisplayedDate(date);
      setIsOpen(false);
      setLocalState(date);
      onChange && onChange(date);
    };

    const { startDay, totalDays } = getMonthData(year, month);
    const { totalDays: prevMonthTotalDays } = getMonthData(
      month - 1 > 0 ? year : year - 1,
      month - 1 > 0 ? month - 1 : 12,
    );

    const validateTypedDate = (e: any) => {
      if (!isValidDateString(e.target.value)) {
        setDisplayedDate(undefined);
      }
    };

    useEffect(() => {
      if (isValidDateString(value ?? '')) {
        setLocalState(value);
        setDisplayedDate(value);
      }
    }, [value]);

    return (
      <Dropdown
        disabled={readOnly}
        open={isOpen}
        trigger={['click']}
        destroyOnHidden
        onOpenChange={setIsOpen}
        popupRender={() => (
          <Card
            onMouseLeave={() => {
              setIsCardFocused(false);
              setActivePlaceholder(defaultPlaceholder);
            }}
            size="small"
            style={{ width: 260 }}
            styles={{
              header: {
                // padding: 3,
                height: 30,
                paddingBlock: 0,
                backgroundColor: '#10a96ff1',
                paddingInline: 10,
                color: 'white',
              },
              // body: { margin: '8px 18px' },
              actions: {
                margin: 0,
                padding: 0,
              },
            }}
            title={
              <CalendarHeader
                localDate={localState ?? getTodayDate()}
                onChange={setLocalState}
              />
            }
            actions={[
              <div
                key="today"
                onMouseEnter={() => setIsCardFocused(false)}
                onClick={() =>
                  isBetween(today, disabledBefore, disabledAfter)
                    ? onDateClicked(today)
                    : undefined
                }
                className={`${
                  isBetween(today, disabledBefore, disabledAfter)
                    ? `tw-text-[#10a96ff1] tw-font-[500] hover:tw-text-[#1CADE3]`
                    : 'tw-text-[#BFBFBF] tw-font-[500] tw-cursor-not-allowed'
                }`}
              >
                Today
              </div>,
            ]}
          >
            <div className="tw-grid tw-grid-cols-7">
              {NEPALI_DAYS.map((day, index) => (
                <div className={containerStyle} key={index}>
                  <div className={`${baseItemStyle}`}>{day}</div>
                </div>
              ))}
            </div>

            <div
              className="tw-grid tw-grid-cols-7"
              onMouseEnter={() => setIsCardFocused(true)}
            >
              {prevMonthTotalDays &&
                Array.from({ length: startDay! }, (_, index) => {
                  const newMonth = month === 1 ? 12 : month - 1;
                  const newYear = month === 1 ? year - 1 : year;
                  const newDate = prevMonthTotalDays - startDay! + index + 1;

                  const currentDate = formatDate(newYear, newMonth, newDate);
                  const isDisabled = !isBetween(
                    currentDate,
                    disabledBefore,
                    disabledAfter,
                  );
                  const isSelected = currentDate === localState;

                  const handleClick = () =>
                    !isDisabled && onDateClicked(currentDate);

                  return (
                    <Tooltip
                      key={index}
                      placement="bottomLeft"
                      title={currentDate}
                    >
                      <div
                        className={
                          isDisabled
                            ? disabledContainerStyle
                            : isSelected
                              ? selectedStyle
                              : containerStyle
                        }
                      >
                        <div
                          className={`
                          ${isDisabled ? disabledStyle : inactiveStyle}
                          ${baseItemStyle}
                        `}
                          onClick={handleClick}
                          onMouseEnter={() => setActivePlaceholder(currentDate)}
                        >
                          {prevMonthTotalDays ? newDate : ''}
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}

              {totalDays &&
                Array.from({ length: totalDays }, (_, index) => {
                  const currentDate = formatDate(year, month, index + 1);
                  const isDisabled = !isBetween(
                    currentDate,
                    disabledBefore,
                    disabledAfter,
                  );
                  const isSelected = currentDate === localState;
                  const isToday = currentDate === today;

                  const handleClick = () =>
                    !isDisabled && onDateClicked(currentDate);

                  const adDate = currentDate ? getAdFromBS(currentDate) : null;
                  const formattedDate = adDate
                    ? getAdFromBSInYMD(adDate)
                    : null;
                  const adDay = formattedDate
                    ? formattedDate.split('-')[2]
                    : null;

                  return (
                    <Tooltip
                      key={index}
                      placement="bottomLeft"
                      title={currentDate}
                    >
                      <div
                        className={
                          isDisabled ? disabledContainerStyle : containerStyle
                        }
                      >
                        <div
                          className={`
                          ${
                            isDisabled
                              ? disabledStyle
                              : isSelected
                                ? selectedStyle
                                : isToday
                                  ? todayStyle
                                  : activeStyle
                          }
                          ${baseItemStyle}
                        `}
                          onClick={handleClick}
                          onMouseEnter={() => setActivePlaceholder(currentDate)}
                        >
                          {/* {index + 1} */}
                          {/* {toDevanagari(index + 1)}*/}

                          <span>
                            {toDevanagari(index + 1)}
                            {adDay && (
                              <sub
                                style={{
                                  fontSize: '0.70em',
                                  color:
                                    isToday || isSelected
                                      ? '#ffffff'
                                      : '#808080',
                                  marginLeft: '1px',
                                }}
                              >
                                {parseInt(adDay, 10)}
                              </sub>
                            )}
                          </span>
                          {/* </span> */}
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}

              {Array.from(
                { length: 42 - (startDay! + totalDays!) },
                (_, index) => {
                  const newMonth = month === 12 ? 1 : month + 1;
                  const newYear = month === 12 ? year + 1 : year;
                  const newDate = index + 1;

                  const currentDate = formatDate(newYear, newMonth, newDate);
                  const isDisabled = !isBetween(
                    currentDate,
                    disabledBefore,
                    disabledAfter,
                  );
                  const isSelected = currentDate === localState;

                  const handleClick = () =>
                    !isDisabled && onDateClicked(currentDate);

                  return (
                    <Tooltip
                      key={index}
                      placement="bottomLeft"
                      title={currentDate}
                    >
                      <div
                        className={
                          isDisabled
                            ? disabledContainerStyle
                            : isSelected
                              ? selectedStyle
                              : containerStyle
                        }
                      >
                        <div
                          className={`
                          ${isDisabled ? disabledStyle : inactiveStyle}
                          ${baseItemStyle}
                        `}
                          onClick={handleClick}
                          onMouseEnter={() => setActivePlaceholder(currentDate)}
                        >
                          {newDate}
                        </div>
                      </div>
                    </Tooltip>
                  );
                },
              )}
            </div>
          </Card>
        )}
      >
        <Input
          addonBefore={props.prefixName ? props.prefixName : null}
          ref={ref}
          suffix={<CalendarOutlined className="!tw-text-[#808080]" />}
          className={`tw-w-full tw-bg-[#ffffff] ${props.className}`}
          style={{
            borderRadius: '0.5rem',
            paddingLeft: '0.5ren',
            paddingRight: '0.5rem',
          }}
          onKeyDown={(e) => onKeyDown?.(e)}
          disabled={props?.disabled || false}
          value={isOpen && isCardFocused ? undefined : displayDate}
          onChange={changeDate}
          onBlur={validateTypedDate}
          placeholder={activePlaceholder}
          allowClear={props?.allowClear ?? true}
          onPressEnter={(e: any) => {
            e.preventDefault();
            // setIsOpen((open) => !open);
            setIsOpen(false);
            selectRef?.current?.focus();
          }}
        />
      </Dropdown>
    );
  },
);

export default NepaliDatePickerComponent;
